import { NEXHANCE_SUPABASE_URL } from '../../shared/supabase-project';

export type SupabaseAuthUser = {
  id: string;
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
  providerData: { providerId: string }[];
};

export type SupabaseSession = {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  expires_at: number;
  user: SupabaseAuthUser;
};

type RawUser = {
  id?: string;
  email?: string | null;
  email_confirmed_at?: string | null;
  app_metadata?: { provider?: string; providers?: string[] };
  user_metadata?: Record<string, unknown>;
};

type RawSession = {
  access_token?: string;
  refresh_token?: string;
  token_type?: string;
  expires_in?: number;
  expires_at?: number;
  user?: RawUser | null;
};

const supabaseUrl = NEXHANCE_SUPABASE_URL;
const supabaseKey = String(import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? import.meta.env.VITE_SUPABASE_ANON_KEY ?? '').trim();
const storageKey = 'nexhance-supabase-session-v1';
const oauthNextKey = 'nexhance-supabase-oauth-next-v1';

export const supabaseEnabled = Boolean(supabaseKey);

export type SupabaseAccountProfile = {
  id: string;
  email: string;
  plan: string;
  credits: number;
  monthlyCreditAllowance: number;
  subscriptionStatus?: string | null;
  billingInterval?: 'monthly' | 'yearly' | null;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: string | null;
};

type RawProfileRow = {
  id?: string;
  email?: string | null;
  plan?: string | null;
  credits?: number | null;
  monthly_credit_allowance?: number | null;
  subscription_status?: string | null;
  billing_interval?: 'monthly' | 'yearly' | null;
  cancel_at_period_end?: boolean | null;
  current_period_end?: string | null;
};

function mapAccountProfile(row: RawProfileRow): SupabaseAccountProfile | null {
  if (!row.id || typeof row.credits !== 'number') return null;
  return {
    id: row.id,
    email: row.email ?? '',
    plan: row.plan ?? 'free',
    credits: Math.max(0, Math.floor(row.credits)),
    monthlyCreditAllowance: Math.max(0, Math.floor(Number(row.monthly_credit_allowance ?? 0))),
    subscriptionStatus: row.subscription_status ?? null,
    billingInterval: row.billing_interval ?? null,
    cancelAtPeriodEnd: Boolean(row.cancel_at_period_end),
    currentPeriodEnd: row.current_period_end ?? null
  };
}

function authNetworkError(error: unknown) {
  if (error instanceof Error && /failed to fetch|networkerror|load failed|name_not_resolved/i.test(error.message)) {
    return new Error(`Could not reach Supabase at ${supabaseUrl}. The project URL is pinned in this build; check your internet/DNS or Supabase project status.`);
  }
  return error instanceof Error ? error : new Error('Could not reach Supabase Authentication.');
}

function authHeaders(token?: string) {
  return {
    apikey: supabaseKey,
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
}

async function authFetch(input: string, init: RequestInit = {}) {
  try {
    return await fetch(input, init);
  } catch (error) {
    throw authNetworkError(error);
  }
}

function providerData(raw: RawUser) {
  const providers = raw.app_metadata?.providers?.length
    ? raw.app_metadata.providers
    : raw.app_metadata?.provider
      ? [raw.app_metadata.provider]
      : ['email'];
  return [...new Set(providers)].map((provider) => ({
    providerId: provider === 'email' ? 'password' : provider === 'google' ? 'google.com' : provider
  }));
}

function mapUser(raw?: RawUser | null): SupabaseAuthUser | null {
  if (!raw?.id) return null;
  const metadata = raw.user_metadata ?? {};
  const displayName = String(metadata.full_name ?? metadata.name ?? metadata.display_name ?? '').trim() || null;
  const photoURL = String(metadata.avatar_url ?? metadata.picture ?? '').trim() || null;
  return {
    id: raw.id,
    uid: raw.id,
    email: raw.email ?? null,
    displayName,
    photoURL,
    emailVerified: Boolean(raw.email_confirmed_at),
    providerData: providerData(raw)
  };
}

function normalizeSession(raw: RawSession): SupabaseSession | null {
  const user = mapUser(raw.user);
  if (!raw.access_token || !raw.refresh_token || !user) return null;
  const expiresIn = Number(raw.expires_in ?? 3600);
  return {
    access_token: raw.access_token,
    refresh_token: raw.refresh_token,
    token_type: raw.token_type ?? 'bearer',
    expires_in: Number.isFinite(expiresIn) ? expiresIn : 3600,
    expires_at: Number(raw.expires_at ?? Math.floor(Date.now() / 1000) + expiresIn),
    user
  };
}

async function readError(response: Response) {
  const body = await response.json().catch(() => ({})) as { msg?: string; message?: string; error_description?: string; error?: string };
  return body.msg || body.message || body.error_description || body.error || `Authentication request failed (${response.status}).`;
}

function saveSession(session: SupabaseSession | null) {
  if (!session) localStorage.removeItem(storageKey);
  else localStorage.setItem(storageKey, JSON.stringify(session));
}

export function getStoredSession(): SupabaseSession | null {
  if (!supabaseEnabled) return null;
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey) || 'null') as SupabaseSession | null;
    return parsed?.access_token && parsed?.refresh_token && parsed?.user?.id ? parsed : null;
  } catch {
    localStorage.removeItem(storageKey);
    return null;
  }
}

export async function refreshSupabaseSession(session: SupabaseSession): Promise<SupabaseSession | null> {
  if (!supabaseEnabled) return null;
  const response = await authFetch(`${supabaseUrl}/auth/v1/token?grant_type=refresh_token`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ refresh_token: session.refresh_token })
  });
  if (!response.ok) {
    saveSession(null);
    return null;
  }
  const next = normalizeSession(await response.json() as RawSession);
  saveSession(next);
  return next;
}

export async function getValidSession(forceRefresh = false): Promise<SupabaseSession | null> {
  const session = getStoredSession();
  if (!session) return null;
  const expiresSoon = session.expires_at <= Math.floor(Date.now() / 1000) + 90;
  if (!forceRefresh && !expiresSoon) return session;
  return refreshSupabaseSession(session);
}

export async function signInWithPassword(email: string, password: string) {
  const response = await authFetch(`${supabaseUrl}/auth/v1/token?grant_type=password`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify({ email: email.trim(), password })
  });
  if (!response.ok) throw new Error(await readError(response));
  const session = normalizeSession(await response.json() as RawSession);
  if (!session) throw new Error('Supabase did not return a valid session.');
  saveSession(session);
  return session;
}

export async function signUpWithPassword(email: string, password: string) {
  const redirectTo = `${window.location.origin}/auth`;
  const response = await authFetch(`${supabaseUrl}/auth/v1/signup?redirect_to=${encodeURIComponent(redirectTo)}`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify({ email: email.trim(), password })
  });
  if (!response.ok) throw new Error(await readError(response));
  const body = await response.json() as RawSession & RawUser;
  const session = normalizeSession(body);
  if (session) saveSession(session);
  return { session, needsEmailConfirmation: !session };
}

export async function signInWithGoogle(nextPath = '/tools') {
  if (!supabaseEnabled) throw new Error('Supabase Authentication is not configured.');
  // Keep redirect_to exact so production can use a strict Supabase allow-list.
  // The intended in-app destination survives the OAuth round trip in sessionStorage.
  sessionStorage.setItem(oauthNextKey, nextPath);
  const redirectTo = `${window.location.origin}/auth`;

  // Fail on the app page with a useful message instead of navigating users to a
  // dead/mistyped project hostname. This catches the common Hostinger VITE_* typo.
  const settings = await authFetch(`${supabaseUrl}/auth/v1/settings`, {
    method: 'GET',
    headers: authHeaders()
  });
  if (!settings.ok) throw new Error(await readError(settings));

  const url = `${supabaseUrl}/auth/v1/authorize?provider=google&redirect_to=${encodeURIComponent(redirectTo)}`;
  window.location.assign(url);
}

export function getStoredOAuthNextPath() {
  try { return sessionStorage.getItem(oauthNextKey); } catch { return null; }
}

export function clearStoredOAuthNextPath() {
  try { sessionStorage.removeItem(oauthNextKey); } catch { /* no-op */ }
}

export function consumeOAuthHash(): SupabaseSession | null {
  if (!window.location.hash.includes('access_token=')) return null;
  const params = new URLSearchParams(window.location.hash.slice(1));
  const raw: RawSession = {
    access_token: params.get('access_token') ?? undefined,
    refresh_token: params.get('refresh_token') ?? undefined,
    token_type: params.get('token_type') ?? undefined,
    expires_in: Number(params.get('expires_in') ?? 3600)
  };
  const token = raw.access_token;
  if (!token) return null;
  try {
    const encoded = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = encoded.padEnd(Math.ceil(encoded.length / 4) * 4, '=');
    const payload = JSON.parse(atob(padded)) as Record<string, unknown>;
    raw.expires_at = Number(payload.exp ?? Math.floor(Date.now() / 1000) + Number(raw.expires_in ?? 3600));
    raw.user = {
      id: String(payload.sub ?? ''),
      email: typeof payload.email === 'string' ? payload.email : null,
      email_confirmed_at: payload.email ? new Date().toISOString() : null,
      app_metadata: (payload.app_metadata as RawUser['app_metadata']) ?? {},
      user_metadata: (payload.user_metadata as RawUser['user_metadata']) ?? {}
    };
  } catch {
    return null;
  }
  const session = normalizeSession(raw);
  saveSession(session);
  window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}`);
  return session;
}

export async function getSupabaseAccountProfile(session: SupabaseSession): Promise<SupabaseAccountProfile> {
  if (!supabaseEnabled) throw new Error('Supabase is not configured.');
  const select = [
    'id', 'email', 'plan', 'credits', 'monthly_credit_allowance',
    'subscription_status', 'billing_interval', 'cancel_at_period_end', 'current_period_end'
  ].join(',');

  async function selectOwnProfile() {
    const response = await authFetch(
      `${supabaseUrl}/rest/v1/profiles?id=eq.${encodeURIComponent(session.user.id)}&select=${encodeURIComponent(select)}&limit=1`,
      { method: 'GET', headers: authHeaders(session.access_token), cache: 'no-store' }
    );
    if (!response.ok) throw new Error(await readError(response));
    const rows = await response.json() as RawProfileRow[];
    return rows[0] ? mapAccountProfile(rows[0]) : null;
  }

  let profile = await selectOwnProfile();
  if (profile) return profile;

  // Safe bootstrap RPC: it uses auth.uid() inside Postgres and can only create
  // the caller's own profile with the fixed one-time free allowance. It cannot
  // accept a user id or arbitrary credit amount from the browser.
  const bootstrap = await authFetch(`${supabaseUrl}/rest/v1/rpc/nexhance_bootstrap_my_profile`, {
    method: 'POST',
    headers: authHeaders(session.access_token),
    body: '{}'
  });
  if (!bootstrap.ok) throw new Error(await readError(bootstrap));
  const bootstrapped = await bootstrap.json() as RawProfileRow[];
  profile = bootstrapped[0] ? mapAccountProfile(bootstrapped[0]) : null;
  if (!profile) profile = await selectOwnProfile();
  if (!profile) throw new Error('Your credit profile could not be created. Run the Nexhance credit-system SQL migration in Supabase.');
  return profile;
}

export async function updateSupabaseDisplayName(session: SupabaseSession, displayName: string) {
  const response = await authFetch(`${supabaseUrl}/auth/v1/user`, {
    method: 'PUT', headers: authHeaders(session.access_token), body: JSON.stringify({ data: { display_name: displayName, full_name: displayName } })
  });
  if (!response.ok) throw new Error(await readError(response));
  const raw = await response.json() as RawUser;
  const user = mapUser(raw);
  if (!user) throw new Error('Supabase returned an incomplete profile.');
  const next = { ...session, user };
  saveSession(next);
  return next;
}

export async function sendSupabasePasswordReset(email: string) {
  const response = await authFetch(`${supabaseUrl}/auth/v1/recover`, {
    method: 'POST', headers: authHeaders(), body: JSON.stringify({ email, redirect_to: `${window.location.origin}/auth?mode=recovery` })
  });
  if (!response.ok) throw new Error(await readError(response));
}

export async function updateSupabasePassword(session: SupabaseSession, password: string) {
  if (password.length < 8) throw new Error('Use a password with at least 8 characters.');
  const response = await authFetch(`${supabaseUrl}/auth/v1/user`, {
    method: 'PUT', headers: authHeaders(session.access_token), body: JSON.stringify({ password })
  });
  if (!response.ok) throw new Error(await readError(response));
}

export async function signOutSupabase(session: SupabaseSession | null) {
  try {
    if (session?.access_token) {
      await authFetch(`${supabaseUrl}/auth/v1/logout`, { method: 'POST', headers: authHeaders(session.access_token) });
    }
  } finally {
    saveSession(null);
  }
}
